import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Calendar, Users, Edit3, Save, XCircle, AlertCircle } from 'lucide-react';
import { firebaseService } from '../services/firebaseService';
import { useAuth } from '../contexts/AuthContext';

interface AlunoModalProps {
  aluno: any | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (alunoData: any) => Promise<void>;
  niveis: any[];
  turmas: any[];
}

// Funções de máscara
const maskCPF = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    .substring(0, 14);
};

const maskPhone = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{4,5})(\d{4})$/, '$1-$2')
    .substring(0, 15);
};

const AlunoModal: React.FC<AlunoModalProps> = ({ aluno, isOpen, onClose, onSave, niveis, turmas }) => {
  const { userProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(!aluno); // Se não há aluno, está em modo criação
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [professores, setProfessores] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadProfessores();
    }
  }, [isOpen, userProfile?.empresaCnpj]);

  useEffect(() => {
    if (aluno) {
      setFormData({
        nome: aluno.nome || '',
        email: aluno.email || '',
        telefone: aluno.telefone || '',
        cellular: aluno.cellular || '',
        cpf: aluno.cpf || '',
        dataNascimento: aluno.dataNascimento || '',
        nivelId: aluno.nivelId || '',
        nivel: aluno.nivel || '',
        turmas: Array.isArray(aluno.turmas) ? aluno.turmas : [],
        professorId: aluno.professorId || '',
        diaVencimento: aluno.diaVencimento || 10,
        valorMensalidade: aluno.valorMensalidade || 0,
        bAtivo: aluno.bAtivo !== undefined ? aluno.bAtivo : true,
        progressoNivel: aluno.progressoNivel || 0
      });
      setIsEditing(false);
    } else {
      // Modo criação
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        cellular: '',
        cpf: '',
        dataNascimento: '',
        nivelId: '',
        nivel: '',
        turmas: [],
        professorId: '',
        diaVencimento: 10,
        valorMensalidade: 0,
        bAtivo: true,
        progressoNivel: 0
      });
      setIsEditing(true);
    }
    setErrors({});
  }, [aluno, isOpen]);

  const loadProfessores = async () => {
    if (!userProfile?.empresaCnpj) return;
    try {
      const profs = await firebaseService.getProfessores(userProfile.empresaCnpj);
      setProfessores(profs);
    } catch (error) {
      console.error('Erro ao carregar professores:', error);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateCPF = (cpf: string): boolean => {
    if (!cpf) return true; // CPF é opcional
    const cleanCPF = cpf.replace(/\D/g, '');
    return cleanCPF.length === 11;
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Telefone é opcional se tiver celular
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10;
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.email || formData.email.trim() === '') {
      newErrors.email = 'Email é obrigatório';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.telefone && !formData.cellular) {
      newErrors.telefone = 'Telefone ou celular é obrigatório';
    }

    if (formData.telefone && !validatePhone(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido';
    }

    if (formData.cellular && !validatePhone(formData.cellular)) {
      newErrors.cellular = 'Celular inválido';
    }

    if (formData.cpf && !validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    let processedValue = value;

    // Aplicar máscaras
    if (field === 'cpf') {
      processedValue = maskCPF(value);
    } else if (field === 'telefone' || field === 'cellular') {
      processedValue = maskPhone(value);
    }

    setFormData((prev: any) => ({
      ...prev,
      [field]: processedValue
    }));

    // Limpar erro do campo quando começar a digitar
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNivelChange = (nivelId: string) => {
    const nivel = niveis.find(n => n.id === nivelId);
    setFormData((prev: any) => ({
      ...prev,
      nivelId: nivelId,
      nivel: nivel?.nome || ''
    }));
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      await onSave({
        ...(aluno || {}),
        ...formData
      });
      setIsEditing(false);
      if (!aluno) {
        // Se estava criando, fecha o modal
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar aluno:', error);
      setErrors({ submit: 'Erro ao salvar aluno. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (aluno) {
      // Modo edição - volta aos dados originais
      setFormData({
        nome: aluno.nome || '',
        email: aluno.email || '',
        telefone: aluno.telefone || '',
        cellular: aluno.cellular || '',
        cpf: aluno.cpf || '',
        dataNascimento: aluno.dataNascimento || '',
        nivelId: aluno.nivelId || '',
        nivel: aluno.nivel || '',
        turmas: Array.isArray(aluno.turmas) ? aluno.turmas : [],
        professorId: aluno.professorId || '',
        diaVencimento: aluno.diaVencimento || 10,
        valorMensalidade: aluno.valorMensalidade || 0,
        bAtivo: aluno.bAtivo !== undefined ? aluno.bAtivo : true,
        progressoNivel: aluno.progressoNivel || 0
      });
      setIsEditing(false);
    } else {
      // Modo criação - fecha o modal
      onClose();
    }
    setErrors({});
  };

  if (!isOpen) return null;

  const isCreating = !aluno;
  const displayName = aluno?.nome || 'Novo Aluno';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').substring(0, 2);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-x-3">
            <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
              {aluno?.fotoUrl ? (
                <img 
                  src={aluno.fotoUrl} 
                  alt={displayName}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <span className="text-lg font-medium text-primary-600">
                  {initials}
                </span>
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {isCreating ? 'Novo Aluno' : (isEditing ? 'Editar Aluno' : 'Detalhes do Aluno')}
              </h2>
              {aluno && <p className="text-sm text-gray-500 dark:text-gray-400">ID: {aluno.id}</p>}
            </div>
          </div>
          <div className="flex items-center gap-x-2">
            {!isEditing && !isCreating ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-x-2 px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <Edit3 className="h-4 w-4" />
                Editar
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-x-2 px-4 py-2 bg-white text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors disabled:opacity-50"
                  disabled={saving}
                >
                  <XCircle className="h-4 w-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
                  disabled={saving}
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={saving}
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>{errors.submit}</span>
            </div>
          )}

          {/* Informações Pessoais */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <User className="h-5 w-5" />
                Informações Pessoais
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome Completo <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => handleInputChange('nome', e.target.value)}
                      className={`input w-full ${errors.nome ? 'border-red-500' : ''}`}
                      placeholder="Nome completo do aluno"
                    />
                    {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{aluno.nome || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`input w-full ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="email@exemplo.com"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{aluno.email || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefone
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="tel"
                      value={formData.telefone}
                      onChange={(e) => handleInputChange('telefone', e.target.value)}
                      className={`input w-full ${errors.telefone ? 'border-red-500' : ''}`}
                      placeholder="(00) 0000-0000"
                    />
                    {errors.telefone && <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{aluno.telefone || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Celular
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="tel"
                      value={formData.cellular}
                      onChange={(e) => handleInputChange('cellular', e.target.value)}
                      className={`input w-full ${errors.cellular ? 'border-red-500' : ''}`}
                      placeholder="(00) 00000-0000"
                    />
                    {errors.cellular && <p className="text-red-500 text-xs mt-1">{errors.cellular}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{aluno.cellular || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CPF
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="text"
                      value={formData.cpf}
                      onChange={(e) => handleInputChange('cpf', e.target.value)}
                      className={`input w-full ${errors.cpf ? 'border-red-500' : ''}`}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                    {errors.cpf && <p className="text-red-500 text-xs mt-1">{errors.cpf}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{aluno.cpf || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data de Nascimento
                </label>
                {isEditing || isCreating ? (
                  <input
                    type="date"
                    value={formData.dataNascimento}
                    onChange={(e) => handleInputChange('dataNascimento', e.target.value)}
                    className="input w-full"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">
                    {aluno?.dataNascimento ? new Date(aluno.dataNascimento).toLocaleDateString('pt-BR') : 'Não informado'}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <Calendar className="h-5 w-5" />
                Informações Acadêmicas
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nível
                </label>
                {isEditing || isCreating ? (
                  <select
                    value={formData.nivelId}
                    onChange={(e) => handleNivelChange(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">Selecione um nível</option>
                    {niveis.map(nivel => (
                      <option key={nivel.id} value={nivel.id}>
                        {nivel.nome}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{aluno?.nivel || 'Não definido'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Progresso no Nível (%)
                </label>
                {isEditing || isCreating ? (
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.progressoNivel}
                    onChange={(e) => handleInputChange('progressoNivel', parseInt(e.target.value) || 0)}
                    className="input w-full"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{aluno?.progressoNivel || 0}%</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Professor Responsável
                </label>
                {isEditing || isCreating ? (
                  <select
                    value={formData.professorId}
                    onChange={(e) => handleInputChange('professorId', e.target.value)}
                    className="input w-full"
                  >
                    <option value="">Selecione um professor</option>
                    {professores.map(prof => (
                      <option key={prof.id} value={prof.id}>
                        {prof.nome}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">
                    {professores.find(p => p.id === aluno?.professorId)?.nome || aluno?.professorId || 'Não definido'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Dia de Vencimento
                </label>
                {isEditing || isCreating ? (
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.diaVencimento}
                    onChange={(e) => handleInputChange('diaVencimento', parseInt(e.target.value) || 10)}
                    className="input w-full"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{aluno?.diaVencimento || 'Não definido'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Valor da Mensalidade (R$)
                </label>
                {isEditing || isCreating ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.valorMensalidade}
                    onChange={(e) => handleInputChange('valorMensalidade', parseFloat(e.target.value) || 0)}
                    className="input w-full"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">
                    {aluno?.valorMensalidade ? `R$ ${aluno.valorMensalidade.toFixed(2)}` : 'Não definido'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Informações Adicionais */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
              <Users className="h-5 w-5" />
              Informações Adicionais
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              {isEditing || isCreating ? (
                <select
                  value={formData.bAtivo ? 'true' : 'false'}
                  onChange={(e) => handleInputChange('bAtivo', e.target.value === 'true')}
                  className="input w-full max-w-xs"
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              ) : (
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  aluno?.bAtivo 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {aluno?.bAtivo ? 'Ativo' : 'Inativo'}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Turmas
              </label>
              {isEditing || isCreating ? (
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  {turmas.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma turma disponível</p>
                  ) : (
                    turmas.map(turma => (
                      <label key={turma.id} className="flex items-center hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={Array.isArray(formData.turmas) && formData.turmas.includes(turma.id)}
                          onChange={(e) => {
                            const currentTurmas = Array.isArray(formData.turmas) ? formData.turmas : [];
                            if (e.target.checked) {
                              handleInputChange('turmas', [...currentTurmas, turma.id]);
                            } else {
                              handleInputChange('turmas', currentTurmas.filter((id: string) => id !== turma.id));
                            }
                          }}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{turma.nome}</span>
                      </label>
                    ))
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Array.isArray(aluno?.turmas) && aluno.turmas.length > 0 ? (
                    aluno.turmas.map((turmaId: string) => {
                      const turma = turmas.find(t => t.id === turmaId);
                      return turma ? (
                        <span key={turmaId} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {turma.nome}
                        </span>
                      ) : null;
                    })
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400">Nenhuma turma</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlunoModal;
